import { getCustomerDisplayName } from "../../utils/customerDisplay";
import CompactReference from "../../components/ui/CompactReference";
import EnumBadge from "../../components/ui/EnumBadge";
import { formatMoney } from "../../utils/formatMoney";
import PurposeBadge from "../../components/ui/PurposeBadge";
import { enumLabel } from "../../components/ui/enumLabel";
import { toast } from "react-toastify";
import { confirmAction } from "../../components/ui/confirmAction";
import DateRangeFilter from "../../components/ui/DateRangeFilter";
import Pagination from "../../components/ui/Pagination";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./TransactionPage.css";
import {
  getTransactions,
  downloadTransactionsPDF,
} from "../../services/transactionService";
import { Transaction } from "../../types/transaction";

// Component & Icon Imports
import TransactionDetailsModal from "../../compoonents/TransactionDetailsModal/TransactionDetailsModal";
import ComingSoonAction from "../../compoonents/ComingSoonAction/ComingSoonAction";
import { FiEye } from "react-icons/fi";
import searchIcon from "../../assets/images/searchicon.png";

import pdfIcon from "../../assets/images/purchaseicon.png";
import csvIcon from "../../assets/images/shipment-icon.png";

type StatusFilter = "all" | "pending" | "successful" | "failed";

const TransactionPage: React.FC = () => {
  // --- STATE MANAGEMENT ---
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeStatus, setActiveStatus] = useState<StatusFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  // --- DATA FETCHING ---
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTransactions(
        activeStatus,
        currentPage,
        10,
        "",
        startDate,
        endDate,
      );
      setTransactions(response.data?.transactions || []);
      setTotalPages(response.data?.pagination?.totalPages ?? 1);
      setTotalItems(response.data?.pagination?.totalItems ?? 0);
    } catch (err) {
      setError("Failed to load transactions.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeStatus, currentPage, startDate, endDate]);

  // Debounce search and fetch on change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTransactions();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchTransactions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeStatus, startDate, endDate]);

  const visibleTransactions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((t) => {
      const name =
        getCustomerDisplayName(t.user).toLowerCase();
      return (
        name.includes(q) ||
        (t.transactionReference || "").toLowerCase().includes(q) ||
        (t.id || "").toLowerCase().includes(q)
      );
    });
  }, [transactions, searchTerm]);

  // --- EXPORT HANDLERS ---

  // 1. CSV Export (Client-Side Generator)
  const handleExportCSV = () => {
    if (transactions.length === 0) {
      toast.warning("No data to export");
      return;
    }

    // Define CSV Headers
    const headers = [
      "Date",
      "Name",
      "Transaction ID",
      "Reference",
      "Category",
      "Amount",
      "Type",
      "Status",
    ];

    // Map data to rows
    const rows = transactions.map((t) => {
      const fullName =
        getCustomerDisplayName(t.user);
      const date = t.createdAt
        ? new Date(t.createdAt).toLocaleDateString()
        : "N/A";
      const amount = t.amount ?? t.amountPaid ?? "";

      return [
        `"${date}"`,
        `"${fullName}"`,
        `"${t.id || ""}"`,
        `"${t.transactionReference || ""}"`,
        `"${t.category || ""}"`,
        `"${amount}"`,
        `"${t.paymentType || ""}"`,
        `"${t.paymentStatus || ""}"`,
      ].join(",");
    });

    // Create CSV content
    const csvContent =
      "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");

    // Trigger Download
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute(
      "download",
      `transactions_${activeStatus}_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 2. PDF Export (Server-Side)
  const handleExportPDF = async () => {
    const confirm = await confirmAction("Download PDF report for current selection?");
    if (!confirm) return;

    try {
      // Assuming backend has a PDF endpoint. If not, this part needs backend work.
      const blob = await downloadTransactionsPDF(activeStatus);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions_report_${activeStatus}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error("Failed to download PDF. Please check if the backend supports PDF generation.");
    }
  };

  // --- RENDER HELPERS ---
  const renderTableContent = () => {
    if (loading) {
      return (
        <tr>
          <td
            colSpan={9}
            className="table-message"
            style={{ textAlign: "center", padding: "20px" }}
          >
            Loading transactions...
          </td>
        </tr>
      );
    }

    if (error) {
      return (
        <tr>
          <td
            colSpan={9}
            className="table-message error"
            style={{ textAlign: "center", padding: "20px", color: "red" }}
          >
            {error}
          </td>
        </tr>
      );
    }

    if (visibleTransactions.length === 0) {
      return (
        <tr>
          <td
            colSpan={9}
            className="table-message"
            style={{ textAlign: "center", padding: "20px" }}
          >
            {searchTerm
              ? `No results for "${searchTerm}"`
              : "No transactions found."}
          </td>
        </tr>
      );
    }

    return visibleTransactions.map((t) => {
      const fullName =
        getCustomerDisplayName(t.user);
      const amount = formatMoney(t.amount ?? t.amountPaid, t.currency);
      const date = t.createdAt
        ? new Date(t.createdAt).toLocaleDateString()
        : "N/A";

      return (
        <tr key={t.id}>
          <td data-label="Name">{fullName}</td>
          <td data-label="Transaction ID">
            <CompactReference value={t.id} />
          </td>
          <td data-label="Transaction Ref"><CompactReference value={t.transactionReference} /></td>
          <td data-label="Purpose"><PurposeBadge value={t.paymentType || t.category} /></td>
          <td data-label="Amount">{amount}</td>
          <td data-label="Date">{date}</td>
          <td data-label="Direction"><span className={`transaction-direction transaction-direction--${t.transactionType === "credit" ? "credit" : "debit"}`}>{t.transactionType ? enumLabel(t.transactionType) : "—"}</span></td>
          <td data-label="Status">
            <EnumBadge value={t.paymentStatus} />
          </td>
          <td data-label="Action">
            <div className="table-action-buttons">
              <button
                type="button"
                className="table-action-btn"
                title="View details"
                aria-label="View details"
                onClick={() => setSelectedTransaction(t)}
              >
                <FiEye />
              </button>
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      <div className="transaction-page">
        <header className="page-header">
          <h3>Transaction</h3>
          <div className="right-controls">
            {/* Export Buttons */}
            <div className="export-buttons">
              <ComingSoonAction title="List PDF export — coming soon">
                <button
                  className="export-btn"
                  type="button"
                  disabled
                  title="Coming soon"
                >
                  PDF <img src={pdfIcon} alt="" />
                </button>
              </ComingSoonAction>
              <button
                className="export-btn"
                onClick={handleExportCSV}
                title="Download CSV"
              >
                CSV <img src={csvIcon} alt="CSV" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="transaction-search-bar">
              <img src={searchIcon} alt="Search" />
              <input
                type="text"
                placeholder="Search name, ref..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </header>

        {/* Status Tabs + Date Range Filter */}
        <div className="filter-row">
          <div className="filter-tabs">
            {(["all", "successful", "pending", "failed"] as StatusFilter[]).map(
              (tab) => (
                <button
                  key={tab}
                  className={activeStatus === tab ? "active" : ""}
                  onClick={() => setActiveStatus(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ),
            )}
          </div>

          <DateRangeFilter startDate={startDate} endDate={endDate} onChange={({ startDate, endDate }) => { setStartDate(startDate); setEndDate(endDate); setCurrentPage(1); }} />
        </div>

        {/* Data Table */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Transaction ID</th>
                <th>Transaction Ref</th>
                <th>Purpose</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Direction</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>{renderTableContent()}</tbody>
          </table>
        </div>

        {/* Pagination */}
        {transactions.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} onPageChange={setCurrentPage} loading={loading} />
        )}
      </div>

      {selectedTransaction && (
        <TransactionDetailsModal
          isOpen={!!selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          transaction={selectedTransaction}
        />
      )}
    </>
  );
};

export default TransactionPage;
